import React, { useEffect, useState, useCallback } from 'react';
import { adminApi } from '../lib/adminApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { ShieldCheck, AlertCircle, Clock, FileText, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  verified: 'bg-green-100 text-green-800',
  pending: 'bg-amber-100 text-amber-800',
  rejected: 'bg-red-100 text-red-800',
};

const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[status] || STATUS_STYLES.pending}`}>
    {status === 'verified' && <ShieldCheck className="w-3 h-3" />}
    {status === 'pending' && <Clock className="w-3 h-3" />}
    {status === 'rejected' && <AlertCircle className="w-3 h-3" />}
    {status || 'pending'}
  </span>
);

const AdminCertificatesPanel = () => {
  const { toast } = useToast();
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? {} : { status: filter };
      const r = await adminApi.listCertificates(params);
      setCerts(r || []);
    } catch (e) {
      toast({ title: 'Failed to load certificates', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (cert, status) => {
    let note = '';
    if (status === 'rejected') {
      note = window.prompt('Reason for rejection (visible to vendor):', '');
      if (note === null) return;
    }
    try {
      await adminApi.verifyCertificate(cert.id, { status, note });
      toast({ title: `Certificate ${status}` });
      await load();
    } catch (e) {
      toast({ title: 'Update failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const deleteCert = async (cert) => {
    if (!window.confirm(`Permanently delete this certificate?`)) return;
    try {
      await adminApi.deleteCertificate(cert.id);
      toast({ title: 'Deleted' });
      await load();
    } catch (e) {
      toast({ title: 'Delete failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const counts = certs.reduce((acc, c) => { acc[c.status] = (acc[c.status] || 0) + 1; return acc; }, {});

  return (
    <div className="space-y-4" data-testid="admin-certificates-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vendor Certificates</h1>
          <p className="text-sm text-gray-500">Review and verify business certificates uploaded by vendors.</p>
        </div>
        <div className="flex gap-2 text-sm">
          {[
            { v: 'all', label: 'All', count: certs.length },
            { v: 'pending', label: 'Pending', count: counts.pending || 0 },
            { v: 'verified', label: 'Verified', count: counts.verified || 0 },
            { v: 'rejected', label: 'Rejected', count: counts.rejected || 0 },
          ].map((opt) => (
            <button
              key={opt.v}
              onClick={() => setFilter(opt.v)}
              className={`px-3 py-1.5 rounded border text-sm ${filter === opt.v ? 'bg-[#ee5e23] text-white border-[#ee5e23]' : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'}`}
              data-testid={`cert-filter-${opt.v}`}
            >
              {opt.label} {filter === 'all' && opt.v !== 'all' ? `(${opt.count})` : ''}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-500 text-sm">Loading…</div>
        ) : certs.length === 0 ? (
          <div className="p-10 text-center text-gray-500 text-sm" data-testid="no-admin-certs">No certificates to show.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-600">
              <tr>
                <th className="px-4 py-2 text-left">Preview</th>
                <th className="px-4 py-2 text-left">Vendor</th>
                <th className="px-4 py-2 text-left">Type</th>
                <th className="px-4 py-2 text-left">Name</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Uploaded</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {certs.map((c) => {
                const abs = c.file_url?.startsWith('http') ? c.file_url : `${BACKEND_URL}${c.file_url}`;
                return (
                  <tr key={c.id} className="border-t border-gray-100" data-testid={`admin-cert-row-${c.id}`}>
                    <td className="px-4 py-2">
                      <a href={abs} target="_blank" rel="noopener noreferrer">
                        {c.file_ext === 'pdf' ? (
                          <div className="h-12 w-12 flex items-center justify-center bg-red-50 text-red-600 border border-gray-200 rounded"><FileText className="w-6 h-6" /></div>
                        ) : (
                          <img src={abs} alt="cert" className="h-12 w-12 object-cover border border-gray-200 rounded" />
                        )}
                      </a>
                    </td>
                    <td className="px-4 py-2 text-gray-900">{c.vendor_name}</td>
                    <td className="px-4 py-2 text-gray-700">{c.cert_type}</td>
                    <td className="px-4 py-2 font-medium text-gray-900">{c.name}</td>
                    <td className="px-4 py-2">
                      <StatusBadge status={c.status} />
                      {c.status === 'rejected' && c.note && <div className="text-[10px] text-red-600 mt-1 max-w-[180px] truncate">{c.note}</div>}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-500">{new Date(c.uploaded_at).toLocaleDateString()}</td>
                    <td className="px-4 py-2 text-right space-x-1 whitespace-nowrap">
                      {c.status !== 'verified' && (
                        <Button size="sm" variant="outline" onClick={() => updateStatus(c, 'verified')} className="text-green-700 border-green-300 hover:bg-green-50" data-testid={`cert-approve-${c.id}`}>
                          <ShieldCheck className="w-4 h-4 mr-1" /> Verify
                        </Button>
                      )}
                      {c.status !== 'rejected' && (
                        <Button size="sm" variant="outline" onClick={() => updateStatus(c, 'rejected')} className="text-amber-700 border-amber-300 hover:bg-amber-50" data-testid={`cert-reject-${c.id}`}>
                          Reject
                        </Button>
                      )}
                      <button onClick={() => deleteCert(c)} className="p-1.5 hover:bg-red-50 rounded text-red-600 inline-flex" title="Delete" data-testid={`cert-delete-${c.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminCertificatesPanel;
